import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, MapPin } from 'lucide-react'
import PageHero from '../components/PageHero'
import SectionNav from '../components/SectionNav'
import { organizations, timeline } from '../data/content'

const filters = [
  { id: 'todos', label: 'Todo' },
  { id: 'mexico', label: 'México' },
  { id: 'mundo', label: 'Mundo' },
]

const History = () => {
  const [filter, setFilter] = useState('todos')
  const events = useMemo(
    () => (filter === 'todos' ? timeline : timeline.filter((event) => event.scope === filter)),
    [filter],
  )

  return (
    <div className="min-h-screen bg-ivory">
      <PageHero
        eyebrow="De la posguerra al Club de Golf México"
        title="Una historia"
        highlight="en dos mapas"
        description="El golf adaptado pasó de terapia de veteranos a un deporte con reglas, ranking y un circuito. México entra en 2012."
      />

      <SectionNav
        items={[
          { href: '#relato', label: 'El relato' },
          { href: '#linea', label: 'Línea de tiempo' },
          { href: '#organismos', label: 'Quién lo rige' },
        ]}
      />

      <section id="relato" className="scroll-mt-36 section-pad mx-auto max-w-3xl">
        <p className="eyebrow">Origen</p>
        <h2 className="display mt-4 text-4xl md:text-5xl">Primero fue rehabilitación. Después, competencia.</h2>
        <div className="mt-8 space-y-5 text-lg leading-relaxed text-mist">
          <p>
            A mediados del siglo XX, la medicina militar encontró en el golf una forma de recuperar equilibrio, fuerza, resistencia y confianza al aire libre. Lo que empezó en hospitales de veteranos terminó en un marco único: la Regla 25, el ranking WR4GD y tours como el G4D.
          </p>
          <p>
            En México el punto de partida es más cercano. En 2012, Alejandro Florero y su hermano, diagnosticado con Parkinson, buscaron cómo seguir jugando. De esa necesidad sale la Asociación Nacional de Golf Adaptado.
          </p>
        </div>
        <p className="mt-8 border-l-2 border-sun pl-5 font-sans text-2xl font-medium italic leading-snug text-ink">
          “El campo dejó de ser un privilegio cuando las reglas reconocieron cuerpos distintos.”
        </p>
      </section>

      <section id="linea" className="scroll-mt-36 bg-cream">
        <div className="section-pad mx-auto max-w-3xl">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="eyebrow">Cronología</p>
              <h2 className="display mt-3 text-4xl">Los años que importan</h2>
            </div>
            <div className="flex gap-2" role="group" aria-label="Filtrar cronología">
              {filters.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  aria-pressed={filter === item.id}
                  onClick={() => setFilter(item.id)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    filter === item.id ? 'bg-leaf text-white' : 'bg-white text-mist hover:text-leaf'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <ol className="mt-12 border-l border-leaf/20">
            {events.map((event) => (
              <li key={`${event.year}-${event.title}`} className="relative pb-10 pl-8 last:pb-0">
                <span className="absolute -left-1.5 top-1.5 h-3 w-3 rounded-full bg-sun ring-4 ring-cream" />
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-serif text-2xl text-leaf">{event.year}</p>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide ${
                    event.scope === 'mexico' ? 'bg-sun text-ink' : 'bg-white text-leaf'
                  }`}>
                    {event.scope === 'mexico' ? 'México' : 'Mundo'}
                  </span>
                </div>
                <h3 className="mt-2 font-serif text-2xl leading-snug text-ink">{event.title}</h3>
                <p className="mt-2 flex items-center gap-1.5 text-sm text-leaf">
                  <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                  {event.place}
                </p>
                <p className="mt-3 leading-relaxed text-mist">{event.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="organismos" className="scroll-mt-36 section-pad mx-auto max-w-7xl">
        <div className="max-w-2xl">
          <p className="eyebrow">Instituciones</p>
          <h2 className="display mt-4 text-4xl md:text-5xl">Quién escribe las reglas y quién las juega.</h2>
        </div>
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {organizations.map((org) => {
            const external = org.href.startsWith('http')
            const Tag = external ? 'a' : Link
            const props = external
              ? { href: org.href, target: '_blank', rel: 'noopener noreferrer' }
              : { to: org.href }
            return (
              <Tag
                key={org.name}
                {...props}
                className="flex h-full flex-col rounded-3xl bg-white p-7 ring-1 ring-leaf/10 transition-shadow hover:shadow-md"
              >
                <span className="text-xs font-semibold uppercase tracking-eyebrow text-leaf">{org.region}</span>
                <h3 className="mt-3 font-serif text-3xl text-ink">{org.name}</h3>
                <p className="mt-1 text-sm text-mist">{org.fullName}</p>
                <p className="mt-4 flex-1 leading-relaxed text-mist">{org.text}</p>
                <span className="mt-6 text-sm font-semibold text-leaf">
                  {external ? 'Sitio oficial' : 'Conocer la asociación'} →
                </span>
              </Tag>
            )
          })}
        </div>
      </section>

      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 rounded-[2rem] bg-fairway px-8 py-14 text-cream md:flex-row md:items-center md:px-14">
          <div className="max-w-xl">
            <p className="eyebrow text-sun">Siguiente capítulo</p>
            <h2 className="mt-3 font-serif text-4xl">Las reglas, el equipo y quién compite hoy.</h2>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link to="/golf" className="btn-brass">
              Ver la Regla 25
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/community" className="btn-secondary">
              Jugadores de México
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default History
